/**
 * Which app a page load starts: the point of sale or the back office. The point of sale is its own
 * app (src/pos), loaded on its own host (pos.ecommify.io, see docs/operations/pos-hosts.md) and at
 * /<handle>/pos on the hub. This file is read before either app loads, so it imports nothing.
 */

/** First path segments that are never a workspace handle in front of /pos. */
const NOT_A_HANDLE = new Set(['admin', 'w', 'portal', 'apps', 'connect', 'supplier', 'track', 'pay', 'invoice', 'statement', 'help', 'api', 'oauth', 'auth', 'sanctum']);

/** Point of sale host names: pos.*, *-pos.ecommify.io and a business's own (VITE_POS_HOSTS). */
export function isPosHostName(host: string, extra: string = String(import.meta.env.VITE_POS_HOSTS ?? '')): boolean {
  const h = host.toLowerCase();
  if (h.startsWith('pos.') || h.endsWith('-pos.ecommify.io')) return true;
  return extra
    .split(',')
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
    .includes(h);
}

/** A workspace's point of sale path: /<handle>/pos and everything under it. */
export function isWorkspacePosPath(pathname: string): boolean {
  const m = /^\/([^/]+)\/pos(?:\/|$)/.exec(pathname);
  return !!m && !NOT_A_HANDLE.has(m[1]!.toLowerCase());
}

/**
 * Paths the point of sale app serves on the hub: a workspace's till and store screens, the
 * business picker (/pos), a paired customer display (/display), the customer's phone (/m/<code>)
 * a store receipt (/receipt/<token>) and a customer's layby (/layby/<token>).
 */
export function isPosAppPath(pathname: string): boolean {
  if (pathname === '/pos' || pathname.startsWith('/pos/')) return true;
  if (pathname === '/display' || pathname.startsWith('/display/')) return true;
  if (pathname.startsWith('/m/') || pathname.startsWith('/receipt/') || pathname.startsWith('/layby/')) return true;
  return isWorkspacePosPath(pathname);
}

/** Whether this page load is the point of sale: every path on a POS host, the POS paths elsewhere. */
export function startsPosApp(loc: { hostname: string; pathname: string }): boolean {
  return isPosHostName(loc.hostname) || isPosAppPath(loc.pathname);
}

/**
 * The hub (back office) for a page on a point of sale host: uat-pos → uat-hub, pos.ecommify.io →
 * hub.ecommify.io; a business's own POS name uses VITE_HUB_ORIGIN (else the production hub). On
 * the hub itself: this origin.
 */
export function hubOrigin(loc: { hostname: string; origin: string; protocol: string } = location, configured: string = String(import.meta.env.VITE_HUB_ORIGIN ?? '')): string {
  const h = loc.hostname.toLowerCase();
  if (!isPosHostName(h)) return loc.origin;
  if (h.endsWith('-pos.ecommify.io')) return `${loc.protocol}//${h.replace(/-pos\.ecommify\.io$/, '-hub.ecommify.io')}`;
  if (h === 'pos.ecommify.io') return `${loc.protocol}//hub.ecommify.io`;
  return configured.replace(/\/$/, '') || 'https://hub.ecommify.io';
}

/** A back-office address as a full URL from the point of sale (same origin on the hub). */
export function backOfficeUrl(path: string, loc: { hostname: string; origin: string; protocol: string } = location): string {
  const origin = hubOrigin(loc);
  return origin === loc.origin ? path : `${origin}${path}`;
}
